import { AxiosError } from 'axios';
import { ServerResponse, WithAuditDetails } from 'data/types';
import { History } from 'history';
import { Dispatch, Action } from 'redux';
import { RootState } from 'data/root.reducers';
import i18n from 'i18n';
import { showSuccessMessage, showError } from 'shared/toaster';
import { ThunkDispatch } from 'redux-thunk';
import { extractErrorMessage } from '../../utils';
import * as api from './alarmNotificationProfile.api';
import { getSelectedAlarmNotificationProfile } from './alarmNotificationProfile.selectors';
import {
  GET_ALARM_NOTIFICATION_PROFILE_LIST,
  GET_ALARM_NOTIFICATION_PROFILE,
  CREATE_ALARM_NOTIFICATION_PROFILE,
  UPDATE_ALARM_NOTIFICATION_PROFILE,
  DELETE_ALARM_NOTIFICATION_PROFILE,
  GO_TO_ALARM_NOTIFICATION_PROFILE_LIST,
  GO_TO_ALARM_NOTIFICATION_PROFILE_EDIT,
  GO_TO_ALARM_NOTIFICATION_PROFILE_VIEW,
  GO_TO_ALARM_NOTIFICATION_PROFILE_CREATE,
  SELECT_ALARM_NOTIFICATION_PROFILE,
  GET_INTERCEPT_ALARM_LIST,
  GET_ALARMS_INST_LIST,
  GET_ALARMS_DEF_LIST,
  DeleteAlarmNotificationProfileThunkAction,
  UpdateAlarmNotificationProfileThunkAction,
  CreateAlarmNotificationProfileThunkAction,
  SET_ALARM_NOTIFICATION_PROFILE_LOADING,
  SetAlarmNotificationProfileLoadingAction,
  AlarmNotificationProfileActionTypes,
  AlarmNotificationProfile,
  SelectAlarmNotificationProfileAction,
  AdaptedAlarmNotificationProfile,
} from './alarmNotificationProfile.types';
import { ALARM_NOTIFICATION_PROFILE, SYSTEM_CONFIGURATION_ROUTE, SYSTEM } from '../../constants';

export function goToAlarmNotificationProfileList(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_ALARM_NOTIFICATION_PROFILE_LIST,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${SYSTEM}${ALARM_NOTIFICATION_PROFILE}`);
  };
}

export function goToAlarmNotificationProfileCreate(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_ALARM_NOTIFICATION_PROFILE_CREATE,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${SYSTEM}${ALARM_NOTIFICATION_PROFILE}/create`);
  };
}

export function goToAlarmNotificationProfileEdit(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_ALARM_NOTIFICATION_PROFILE_EDIT,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${SYSTEM}${ALARM_NOTIFICATION_PROFILE}/edit`);
  };
}

export function goToAlarmNotificationProfileView(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_ALARM_NOTIFICATION_PROFILE_VIEW,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${SYSTEM}${ALARM_NOTIFICATION_PROFILE}/view`);
  };
}

export function setAlarmNotificationProfileLoadingAction(
  isLoading: boolean,
): SetAlarmNotificationProfileLoadingAction {
  return {
    type: SET_ALARM_NOTIFICATION_PROFILE_LOADING,
    payload: { isLoading },
  };
}

export function getAlarmNotificationProfileList(): AlarmNotificationProfileActionTypes {
  return {
    type: GET_ALARM_NOTIFICATION_PROFILE_LIST,
    payload: api.getAlarmNotificationProfileList(),
  };
}

export function getInterceptAlarmList(params?: string): AlarmNotificationProfileActionTypes {
  return {
    type: GET_INTERCEPT_ALARM_LIST,
    payload: api.getInterceptAlarmList(params),
  };
}

export function getAlarmsInstList(): AlarmNotificationProfileActionTypes {
  return {
    type: GET_ALARMS_INST_LIST,
    payload: api.getAlarmsInstList(),
  };
}

export function getAlarmsDefList(): AlarmNotificationProfileActionTypes {
  return {
    type: GET_ALARMS_DEF_LIST,
    payload: api.getAlarmsDefList(),
  };
}

export function getAlarmNotificationProfile(
  id: string,
): AlarmNotificationProfileActionTypes {
  return {
    type: GET_ALARM_NOTIFICATION_PROFILE,
    payload: api.getAlarmNotificationProfile(id),
  };
}

export function createAlarmNotificationProfile(
  body: WithAuditDetails<AlarmNotificationProfile>,
  history: History,
): CreateAlarmNotificationProfileThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    try {
      await dispatch({
        type: CREATE_ALARM_NOTIFICATION_PROFILE,
        payload: api.createAlarmNotificationProfile(body),
      });

      dispatch(getAlarmNotificationProfileList());
      dispatch(goToAlarmNotificationProfileList(history));
      showSuccessMessage(i18n.t('alarmNotificationProfile.create.success', { name: body.data?.name }), 5000);

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('alarmNotificationProfile.create.failed', { name: body.data?.name, error: errorMessage }));
    }
  };
}

export function createAlarmNotificationProfileFromForm(
  body: WithAuditDetails<AlarmNotificationProfile>,
): CreateAlarmNotificationProfileThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    dispatch(setAlarmNotificationProfileLoadingAction(true));
    try {
      await dispatch({
        type: CREATE_ALARM_NOTIFICATION_PROFILE,
        payload: api.createAlarmNotificationProfile(body),
      });

      showSuccessMessage(i18n.t('alarmNotificationProfile.create.success', { name: body.data?.name }), 5000);

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('alarmNotificationProfile.create.failed', { name: body.data?.name, error: errorMessage }));

    } finally {
      dispatch(setAlarmNotificationProfileLoadingAction(false));
      dispatch(getAlarmNotificationProfileList());
    }
  };
}

export function updateAlarmNotificationProfile(
  id: string,
  body: WithAuditDetails<AlarmNotificationProfile>,
  history: History,
): UpdateAlarmNotificationProfileThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    try {
      await dispatch({
        type: UPDATE_ALARM_NOTIFICATION_PROFILE,
        payload: api.updateAlarmNotificationProfile(id, body),
      });

      dispatch(getAlarmNotificationProfileList());
      dispatch(goToAlarmNotificationProfileList(history));
      showSuccessMessage(i18n.t('alarmNotificationProfile.update.success', { name: body.data?.name }), 5000);

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('alarmNotificationProfile.update.failed', { name: body.data?.name, error: errorMessage }));
    }
  };
}

export function deleteAlarmNotificationProfile(
  id: string,
  body: WithAuditDetails<void>,
  history?: History,
): DeleteAlarmNotificationProfileThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>, getState: () => RootState) => {
    const [selectedAlarmNotificationProfile] = getSelectedAlarmNotificationProfile(getState());

    try {
      await dispatch({
        type: DELETE_ALARM_NOTIFICATION_PROFILE,
        payload: api.deleteAlarmNotificationProfile(id, body),
      });

      showSuccessMessage(i18n.t('alarmNotificationProfile.delete.success', { name: selectedAlarmNotificationProfile?.name }), 5000);
      if (history != null) {
        dispatch(goToAlarmNotificationProfileList(history));
      }
    } catch (e) {
      showError(i18n.t('alarmNotificationProfile.delete.failed', { name: selectedAlarmNotificationProfile?.name }));

    } finally {
      dispatch(getAlarmNotificationProfileList());
    }
  };
}

export function selectAlarmNotificationProfile(
  alarmNotificationProfile: AdaptedAlarmNotificationProfile[],
): SelectAlarmNotificationProfileAction {
  return {
    type: SELECT_ALARM_NOTIFICATION_PROFILE,
    payload: alarmNotificationProfile,
  };
}
